import { computed, defineComponent, onMounted, ref } from "vue";
import { arrayFindIndexOrFail, nextGlobalIntlike, sortByAccentAwareCaseInsenstive } from "src/helpers/utils";
import { GlobalInteractionBlockingRequestsInFlight } from "src/store/EventuallyPinia";
import { createEventSignupLabel, deleteEventSignupLabels, EventLabel, listEventSignupLabels, updateEventSignupLabel } from "src/composables/InleagueApiV1.Event";
import { axiosAuthBackgroundInstance, AxiosErrorWrapper, axiosInstance } from "src/boot/axios";
import { EditEventLabelModal, EventLabelEditor, EventLabelFormData, eventLabelFormFromExisting, nextLocalEventLabelID } from "./EventLabels.02";
import { ColDef, freshSortState } from "src/modules/TableUtils";
import { AutoModal, DefaultModalController_r, DefaultTinySoccerballBusyOverlay, ModalUtil, useDefaultNoCloseModalIfBusy } from "../UserInterface/Modal";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import { BasicAutoTable2, BasicAutoTable2Slots, typedBasicAutoTable2Props } from "src/modules/BasicAutoTable";
import { Btn2, btn2_redEnabledClasses } from "../UserInterface/Btn2";
import { SoccerBall } from "../SVGs";

export default defineComponent({
  setup() {
    const labels = ref<EventLabelFormData[]>([])
    const toDelete = computed(() => labels.value.filter(v => v.markedForDelete))
    const ready = ref(false)

    onMounted(async () => {
      await GlobalInteractionBlockingRequestsInFlight.withSpinner(async () => {
        const data = await listEventSignupLabels(axiosInstance, {}).then(vs => vs.sort(sortByAccentAwareCaseInsenstive(v => v.label)))
        labels.value = data.map(v => eventLabelFormFromExisting(v))
        ready.value = true
      })
    })

    const confirmDeleteModalController = (() => {
      const {busy, onCloseCB} = useDefaultNoCloseModalIfBusy()

      const doDeleteLabels = async () : Promise<void> => {
        try {
          const toDeleteShallowCopy = [...toDelete.value];

          {
            const toDelete = toDeleteShallowCopy;

            if (toDelete.length === 0) {
              return // shouldn't happen
            }

            const eventLabelIDs = toDelete.map(v => {
              return v.eventLabelID
            });

            await deleteEventSignupLabels(axiosAuthBackgroundInstance, {eventSignupLabelIDs: eventLabelIDs})

            // update local state
            const deleted = new Set(eventLabelIDs)
            labels.value = labels.value.filter(v => !deleted.has(v.eventLabelID))
          }
        }
        catch (err) {
          AxiosErrorWrapper.rethrowIfNotAxiosError
        }
      }

      const confirm = async () : Promise<void> => {
        try {
          try {
            busy.value = true;
            await doDeleteLabels()
          }
          finally {
            busy.value = false
          }
          confirmDeleteModalController.close()
        }
        catch (err) {
          AxiosErrorWrapper.rethrowIfNotAxiosError(err)
        }
      }

      return DefaultModalController_r<void>({
        title: ModalUtil.simpleTitle("Confirm Delete"),
        content: () => {
          return <div>
            <div>Delete {toDelete.value.length === 1 ? "1 label" : `${toDelete.value.length} labels`}?</div>
            <div class="text-sm">Note: The labels will be removed from any signups to which they are currently linked.</div>
            <div class="mt-4 flex items-center gap-2">
              <Btn2 class="px-2 py-1" onClick={() => confirm()} >Confirm</Btn2>
              <Btn2 class="px-2 py-1" enabledClasses={btn2_redEnabledClasses} onClick={() => confirmDeleteModalController.close()}>Cancel</Btn2>
            </div>
            <DefaultTinySoccerballBusyOverlay if={busy.value}/>
          </div>
        }
      }, {onCloseCB})
    })();

    const editEventLabelModalController = (() => {
      const {busy, onCloseCB} = useDefaultNoCloseModalIfBusy()

      const doUpdateLabel = async (freshText: string, obj: EventLabelFormData) : Promise<void> => {
        try {
          try {
            busy.value = true
            await updateEventSignupLabel(axiosAuthBackgroundInstance, {eventSignupLabelID: obj.eventLabelID, label: freshText})
            obj.label = freshText
          }
          finally {
            busy.value = false;
          }
          editEventLabelModalController.close()
        }
        catch (err) {
          AxiosErrorWrapper.rethrowIfNotAxiosError(err)
        }
      }

      return DefaultModalController_r<EventLabelFormData>({
        title: () => <>
          <div>Edit Label</div>
          <div class="my-2 border-b"/>
        </>,
        content: data => {
          if (!data) {
            return null
          }
          return <div>
            <EditEventLabelModal
              label={data}
              existingLabels={labels.value}
              onSubmit={async (freshText) => await doUpdateLabel(freshText, data)}
              onCancel={() => editEventLabelModalController.close()}
            />
            <DefaultTinySoccerballBusyOverlay if={busy.value}/>
          </div>
        }
      }, {onCloseCB})
    })()


    const doAddLabel = async (label: string) : Promise<void> => {
      const ephemeral : EventLabelFormData = {
        __vueKey: nextGlobalIntlike(),
        tentative: true,
        busy: true,
        label,
        eventLabelID: nextLocalEventLabelID(),
        labeledSignupsCount: 0,
        markedForDelete: false,
      }

      const finalize = (replaceWith?: EventLabel) => {
        const idxOfOld = arrayFindIndexOrFail(labels.value, v => v.eventLabelID === ephemeral.eventLabelID)
        if (replaceWith) {
          labels.value.splice(idxOfOld, 1, eventLabelFormFromExisting(replaceWith))
        }
        else {
          labels.value.splice(idxOfOld, 1)
        }
      }

      try {
        labels.value.unshift(ephemeral)
        finalize(await createEventSignupLabel(axiosAuthBackgroundInstance, {label}));
      }
      catch (err) {
        finalize();
        AxiosErrorWrapper.rethrowIfNotAxiosError(err)
      }
    }

    const headerClass = "p-1 border"
    const colDefs : ColDef<EventLabelFormData>[] = [
      {
        id: "delete",
        label: "Delete",
        headerClass,
        html: v => v.busy
          ? null
          : <input type="checkbox" v-model={v.markedForDelete}/>
      },
      {
        id: "edit",
        label: "Edit",
        headerClass,
        html: v => v.busy
          ? null
          : <button
            type="button"
            disabled={v.markedForDelete}
            class="il-buttonlike-1 cursor-pointer rounded-md p-1"
            onClick={() => editEventLabelModalController.open(v)}
          >
            <FontAwesomeIcon icon={faPencil}/>
          </button>
      },
      {
        id: "label",
        label: "Label",
        headerClass,
        html: v => v.busy
          ? <div class="flex items-center gap-2"><SoccerBall/>{v.label}</div>
          : v.label
      },
      {
        id: "signupCount",
        label: "No. Signups Labeled",
        headerClass,
        html: v => v.labeledSignupsCount
      }
    ]
    const sortState = freshSortState(colDefs)

    const tableProps = computed(() => typedBasicAutoTable2Props({
      colDefs: colDefs,
      rows: [...labels.value.values()],
      paginationOptions: [50, "ALL"],
      sortState: sortState,
      rowKey: v => `${v.eventLabelID}`,
      rowAttrs: v => {
        return {
          "data-test": `${v.eventLabelID}`,
        }
      },
      noData: () => <div class="my-2">No labels found.</div>
    }))

    return () => {
      if (!ready.value) {
        return null
      }

      return <div style="--fk-padding-input: .35em;">
        <AutoModal controller={editEventLabelModalController} data-test="editEventLabelModal"/>
        <AutoModal controller={confirmDeleteModalController} data-test="confirmDeleteModal"/>
        <h2 class="mb-2">Event Signup Labels</h2>
        <p class="my-2">Labels can be added to event signups for informational purposes.</p>
        <EventLabelEditor
          labels={labels.value}
          addLabel={doAddLabel}
        >
        </EventLabelEditor>
        <BasicAutoTable2 {...tableProps.value}>
          {{
            immediatelyAfterTable: () => <Btn2
              class="my-2 px-2 py-1"
              disabled={toDelete.value.length === 0}
              onClick={() => confirmDeleteModalController.open()}
            >Delete Selected</Btn2>
          } satisfies BasicAutoTable2Slots}
        </BasicAutoTable2>
      </div>
    }
  }
})
